// frontend/src/pages/guides/GuideFAQ.js
// PRD-193 WP5 (193B) — the VISIBLE Q&A section, and the FAQPage schema built from the SAME
// source array so the two cannot diverge.
//
// Why one module owns both: the WAF guide (the only guide with FAQ schema before this) shipped
// 6 visible questions and 4 schema questions, with different answer text in each. That is
// precisely the divergence PRD-193 calls a fail rather than a shortcut. Here the visible
// section and `buildFAQPageSchema()` read the same guidesCatalog.js `faq` array, so a schema
// question that is not on the page is not expressible.
//
// The VALUE IS THE VISIBLE Q&A, not rich results. Google added a deprecation notice to FAQ
// structured data on 2026-05-07; FAQ rich results no longer appear in Search and the Search
// Console API field is being removed in August 2026. `FAQPage` is still a valid Schema.org
// type that AI retrieval systems parse, so the schema stays as a cheap secondary add -- but
// nothing in this codebase should ever be framed as earning FAQ rich results.
//
// buildFAQPageSchema() is called at each guide's own jsonLd site rather than injected here,
// because SEOHead owns the <script> emission for the whole page. That also keeps the literal
// string `FAQPage` in every guide file, which is the check PRD-193 acceptance criterion 3 runs.

import React from 'react';
import { getGuideByPath } from '../../config/guidesCatalog';

/** Resolve the catalog's `faq` array for a route path. Always an array. */
function faqFor(path, faq) {
  if (Array.isArray(faq)) return faq;
  const guide = path ? getGuideByPath(path) : null;
  return (guide && Array.isArray(guide.faq)) ? guide.faq : [];
}

/**
 * Build the FAQPage JSON-LD for one guide, from the same catalog array the visible section
 * renders. Returns null when the guide has no FAQ entries, so a caller can spread it away
 * without emitting an empty `mainEntity`.
 *
 * @param {string} path  route path, e.g. '/guides/exposed-rdp'
 */
export function buildFAQPageSchema(path, faq) {
  const entries = faqFor(path, faq);
  if (!entries.length) return null;

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": entries.map(item => ({
      "@type": "Question",
      "name": item.q,
      "acceptedAnswer": { "@type": "Answer", "text": item.a },
    })),
  };
}

/**
 * The visible Q&A section. Rendered as a normal `.guide-section` with an `#faq` anchor so it
 * joins each guide's existing table of contents, matching the one hand-built precedent in
 * WAFVulnerabilityScanningGuide.js.
 *
 * @param {string} [path]  route path -- resolves `faq` from the catalog. Preferred.
 * @param {Array}  [faq]   explicit override, for a caller with no catalog entry.
 */
function GuideFAQ({ path, faq }) {
  const entries = faqFor(path, faq);
  if (!entries.length) return null;

  return (
    <section id="faq" className="guide-section guide-faq">
      <h2>Frequently Asked Questions</h2>
      {entries.map((item, i) => (
        <div className="guide-faq-item" key={i}>
          <h3>{item.q}</h3>
          <p>{item.a}</p>
        </div>
      ))}
    </section>
  );
}

export default GuideFAQ;
